import React, { useEffect, useState } from "react";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import {
  Button,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Paper,
  Table,
  TableContainer,
  Typography,
  IconButton,
  Tooltip,
  Container,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Loading } from "../../../components/Loading";
import axios from "axios";
import { useToast } from "../../../components/Toast";
import AddGarageModal from "./AddGarageModal";
import NavigationHeader from "../../../components/NavigationHeader";
import { tableHeaderStyles, tableCellStyles } from "../../../utils/styles";

export interface Garage {
  id?: string;
  name: string;
  contactName: string;
  contactNumber: string;
  location: string;
  companyId?: string;
}

const Garage = () => {
  const [garages, setGarages] = useState<Garage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGarage, setSelectedGarage] = useState<Garage | undefined>();
  const { user, loading: authLoading } = useFirebaseAuth();
  const { showToast } = useToast();

  useEffect(() => {
    if (user && !authLoading) {
      fetchGarageData();
    }
  }, [user, authLoading]);

  const fetchGarageData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}master-data/garages`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        setGarages(response?.data?.data);
      }
    } catch (error) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddGarage = () => {
    setSelectedGarage(undefined);
    setIsModalOpen(true);
  };

  const handleEditGarage = (garage: Garage) => {
    setSelectedGarage(garage);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedGarage(undefined);
  };

  const handleSubmit = async (garageData: any) => {
    setIsSubmitting(true);
    if (selectedGarage) {
      // Update existing garage
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_HOST}master-data/garages/${garageData?.id}`,
          garageData,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Garage updated successfully", "success");
          fetchGarageData();
        } else {
          showToast(`Error updating garage`, "error");
        }
      } catch (error) {
        console.error("Error updating garage", error);
        showToast(`Error updating garage`, "error");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Add new garage
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_HOST}master-data/garages`,
          garageData,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Garage added successfully", "success");
          fetchGarageData();
        } else {
          showToast(`Error submitting data`, "error");
        }
      } catch (error) {
        console.error("Error adding garage", error);
        showToast(`Error adding garage`, "error");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading || authLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting..." fullScreen={false} />
    ) : (
      <Loading message="Loading garages..." fullScreen={false} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Garages & Service Centers" },
        ]}
        title="Garages & Service Centers"
        button={{
          label: "Add Garage",
          onClick: handleAddGarage,
          icon: <AddIcon />,
        }}
      />

      {garages.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
          No garages found
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="garages table">
            <TableHead>
              <TableRow>
                <TableCell {...tableHeaderStyles}>Name</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Person</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Number</TableCell>
                <TableCell {...tableHeaderStyles}>Location</TableCell>
                <TableCell {...tableHeaderStyles} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {garages.map((garage) => (
                <TableRow
                  key={garage.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell {...tableCellStyles}>{garage.name}</TableCell>
                  <TableCell {...tableCellStyles}>
                    {garage.contactName}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {garage.contactNumber}
                  </TableCell>
                  <TableCell {...tableCellStyles}>{garage.location}</TableCell>
                  <TableCell {...tableCellStyles} align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditGarage(garage)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddGarageModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        garage={selectedGarage}
        isEdit={!!selectedGarage}
      />
    </Container>
  );
};

export default Garage;
